/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import api, { setTenantSlug } from '../lib/api'
import { resolveTenantSlug } from '../lib/tenantUtils'

const TenantContext = createContext({})

export const useTenant = () => {
  const context = useContext(TenantContext)
  return context ?? { tenant: null, loading: false }
}

export const TenantProvider = ({ children }) => {
  const [searchParams] = useSearchParams()
  const [tenant, setTenant] = useState(null)
  const [loading, setLoading] = useState(true)

  const slug = useMemo(() => resolveTenantSlug(searchParams), [searchParams])

  useEffect(() => {
    if (!slug) {
      queueMicrotask(() => {
        setTenant(null)
        // Do not call setTenantSlug(null) here: it races AuthContext and clears the super-admin
        // "acting tenant" (localStorage) and tenant admins' X-Tenant right after login, causing empty
        // clients/campaigns and spurious API failures. URL tenant is optional; auth owns the default.
        setLoading(false)
      })
      return
    }

    let cancelled = false
    queueMicrotask(() => setLoading(true))

    api.tenants
      .getBySlug(slug)
      .then((data) => {
        if (!cancelled) {
          setTenant({
            slug: data.slug,
            displayName: data.displayName ?? data.slug,
            logoUrl: data.logoUrl ?? null,
          })
          setTenantSlug(data.slug)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTenant(null)
          setTenantSlug(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [slug])

  const value = useMemo(
    () => ({ tenant, loading }),
    [tenant, loading]
  )

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  )
}
